import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ProfessionalHeader from '../components/ProfessionalHeader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface AdminUser {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  is_advanced_user: boolean;
  is_expert: boolean;
  is_advanced_haiku: boolean;
  is_secret_corner: boolean;
  last_login: string | null;
  created_at: string | null;
}

type FlagKey = Exclude<
  keyof AdminUser,
  'id' | 'email' | 'full_name' | 'last_login' | 'created_at'
>;

/**
 * The access flags, in the order they are shown.
 *
 * `impliedBySuperuser` records the thing that is invisible in the database: core/deps.py
 * reads `flag or is_superuser` for the four access gates, so an administrator already
 * passes them with the column set to false. Without saying so the console looks like a
 * superuser is missing access they actually have. `is_active` is deliberately not in
 * that set -- get_current_active_user rejects an inactive account whatever else it is.
 */
const FLAGS: { key: FlagKey; label: string; hint: string; impliedBySuperuser: boolean }[] = [
  { key: 'is_active', label: 'Active', hint: 'Can sign in at all', impliedBySuperuser: false },
  { key: 'is_advanced_user', label: 'Advanced', hint: '75-field advanced form', impliedBySuperuser: true },
  { key: 'is_expert', label: 'Expert', hint: 'Expert labeling + Fitter’s Corner', impliedBySuperuser: true },
  { key: 'is_secret_corner', label: 'Secret Corner', hint: 'Player’s Secret Corner pass', impliedBySuperuser: true },
  { key: 'is_advanced_haiku', label: 'Haiku vision', hint: 'Launch-monitor cross-check — billed per image', impliedBySuperuser: true },
  { key: 'is_superuser', label: 'Administrator', hint: 'Implies every access above', impliedBySuperuser: false },
];

const authHeaders = () => {
  const storageType = localStorage.getItem('auth_storage_type');
  const storage = storageType === 'session' ? sessionStorage : localStorage;
  return { Authorization: `Bearer ${storage.getItem('access_token')}` };
};

const describeError = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err) && err.response) {
    const detail = err.response.data?.detail;
    if (typeof detail === 'string') return detail;
    return `${fallback}: the server returned ${err.response.status}.`;
  }
  return `${fallback}: could not reach the server.`;
};

/**
 * Admin console for user provisioning and access control.
 *
 * Covers the whole onboarding path without SQL: create an account from an email address,
 * hand over a temporary password, and set any of the six access flags. The password is
 * shown once and never stored in the clear -- losing it means resetting, not looking it up.
 */
const AdminUsersPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [filter, setFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFlags, setNewFlags] = useState<Partial<Record<FlagKey, boolean>>>({});

  // The one place a plaintext password exists after the response. Kept in state, never
  // written to storage, and cleared by hand so it cannot linger behind a filter change.
  const [credential, setCredential] = useState<
    { email: string; password: string; note: string } | null
  >(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/admin/users`, {
        headers: authHeaders(),
        params: { limit: 500 }
      });
      setUsers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(describeError(err, 'Could not load the user list'));
    } finally {
      setLoading(false);
    }
  };

  const showCredential = (email: string, password: string, note: string) => {
    setCredential({ email, password, note });
    setCopied(false);
  };

  const copyPassword = async () => {
    if (!credential) return;
    try {
      await navigator.clipboard.writeText(credential.password);
      setCopied(true);
    } catch {
      // Clipboard access is refused on an insecure origin; the password is on screen
      // anyway, so say so rather than failing silently.
      setError('Could not copy automatically — select the password and copy it by hand.');
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setCreating(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/admin/users/create`,
        {
          email: newEmail.trim(),
          full_name: newName.trim() || null,
          password: newPassword.trim() || null,
          ...newFlags,
        },
        { headers: authHeaders() }
      );
      const created: AdminUser & { temporary_password: string | null } = response.data;
      setUsers(prev => [created, ...prev]);
      showCredential(
        created.email,
        created.temporary_password || newPassword.trim(),
        created.temporary_password
          ? 'Generated for the new account. This is the only time it is shown.'
          : 'The password you chose for the new account.'
      );
      setNewEmail('');
      setNewName('');
      setNewPassword('');
      setNewFlags({});
      setShowCreate(false);
      setError(null);
    } catch (err) {
      console.error('Error creating user:', err);
      setError(describeError(err, `Could not create ${newEmail.trim()}`));
    } finally {
      setCreating(false);
    }
  };

  const toggleFlag = async (target: AdminUser, key: FlagKey) => {
    const next = !target[key];
    const flag = FLAGS.find(f => f.key === key)!;
    if (!window.confirm(`${next ? 'Grant' : 'Remove'} ${flag.label} for ${target.email}?`)) return;

    setPendingId(target.id);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/v1/admin/users/${target.id}/permissions`,
        { [key]: next },
        { headers: authHeaders() }
      );
      // Trust the server's copy of the row rather than flipping the flag locally,
      // so a partially-applied change can never look like it succeeded.
      setUsers(prev => prev.map(u => (u.id === target.id ? response.data : u)));
      setError(null);
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError(describeError(err, `Could not update ${flag.label} for ${target.email}`));
    } finally {
      setPendingId(null);
    }
  };

  const resetPassword = async (target: AdminUser) => {
    if (!window.confirm(
      `Set a new temporary password for ${target.email}? Their current password stops working immediately.`
    )) return;

    setPendingId(target.id);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/admin/users/${target.id}/set-password`,
        {},
        { headers: authHeaders() }
      );
      showCredential(
        response.data.email,
        response.data.password,
        'Their old password no longer works. This is the only time the new one is shown.'
      );
      setError(null);
    } catch (err) {
      console.error('Error setting password:', err);
      setError(describeError(err, `Could not set a password for ${target.email}`));
    } finally {
      setPendingId(null);
    }
  };

  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? users.filter(u =>
        u.email.toLowerCase().includes(needle) ||
        (u.full_name || '').toLowerCase().includes(needle)
      )
    : users;

  const expertCount = users.filter(u => u.is_expert).length;
  const cornerCount = users.filter(u => u.is_secret_corner).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfessionalHeader user={user} onLogout={logout} />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User access</h1>
            <p className="text-sm text-gray-600 mt-1">
              {users.length} account{users.length === 1 ? '' : 's'} · {expertCount} with expert access
              · {cornerCount} with a Secret Corner pass
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setLoading(true); fetchUsers(); }}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Refresh
            </button>
            <button
              onClick={() => setShowCreate(v => !v)}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700"
            >
              {showCreate ? 'Cancel' : 'Add user'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {credential && (
          <div className="mb-6 bg-amber-50 border border-amber-300 rounded-xl px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-amber-900">Temporary password</div>
                <div className="text-sm text-amber-800 break-words">{credential.email}</div>
              </div>
              <button
                onClick={() => setCredential(null)}
                className="text-amber-700 hover:text-amber-900 text-sm shrink-0"
              >
                Dismiss
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <code className="px-3 py-2 bg-white border border-amber-300 rounded-lg font-mono text-base tracking-wide break-all">
                {credential.password}
              </code>
              <button
                onClick={copyPassword}
                className="px-3 py-2 text-sm rounded-lg border border-amber-400 text-amber-900 hover:bg-amber-100"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-3 text-sm text-amber-800">
              {credential.note} Pass it on yourself — nothing is emailed. They can change it
              from their profile once they are in.
            </p>
          </div>
        )}

        {showCreate && (
          <form
            onSubmit={createUser}
            className="mb-6 bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-4"
          >
            <h2 className="font-semibold text-gray-900">New account</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="block text-sm text-gray-700 mb-1">Email</span>
                <input
                  type="email"
                  required
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="fitter@example.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="block text-sm text-gray-700 mb-1">Full name (optional)</span>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
            <label className="block">
              <span className="block text-sm text-gray-700 mb-1">
                Temporary password (optional)
              </span>
              <input
                type="text"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Leave blank to generate one"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <div>
              <span className="block text-sm text-gray-700 mb-2">Access</span>
              <div className="flex flex-wrap gap-2">
                {FLAGS.filter(f => f.key !== 'is_active').map(f => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setNewFlags(prev => ({ ...prev, [f.key]: !prev[f.key] }))}
                    title={f.hint}
                    className={`px-3 py-2 text-sm rounded-lg border ${
                      newFlags[f.key]
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={creating || !newEmail.trim()}
              className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create account'}
            </button>
          </form>
        )}

        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by email or name…"
          className="w-full mb-4 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="space-y-3">
          {visible.map(u => {
            const isOpen = expandedId === u.id;
            const isSelf = u.id === user?.id;
            return (
              <div
                key={u.id}
                className={`bg-white rounded-xl shadow-md ${u.is_active ? '' : 'opacity-60'}`}
              >
                <div className="p-4 flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 break-words">{u.email}</div>
                    <div className="text-sm text-gray-500">
                      {u.full_name || 'No name on file'} · last login{' '}
                      {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'never'}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {u.is_superuser && (
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">Admin</span>
                      )}
                      {u.is_expert && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Expert</span>
                      )}
                      {u.is_advanced_user && (
                        <span className="text-xs px-2 py-1 bg-green/15 text-green-ink rounded">Advanced</span>
                      )}
                      {u.is_secret_corner && (
                        <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">Secret Corner</span>
                      )}
                      {u.is_advanced_haiku && (
                        <span className="text-xs px-2 py-1 bg-cyan/15 text-cyan-ink rounded">Haiku</span>
                      )}
                      {!u.is_active && (
                        <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded">Inactive</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedId(isOpen ? null : u.id)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 shrink-0"
                  >
                    {isOpen ? 'Done' : 'Manage'}
                  </button>
                </div>

                {isOpen && (
                  <div className="border-t border-gray-100 p-4 space-y-3">
                    {FLAGS.map(f => {
                      const on = u[f.key] as boolean;
                      const viaAdmin = !on && f.impliedBySuperuser && u.is_superuser;
                      // Both directions would strip the access needed to undo them, and
                      // the backend refuses them too -- this only saves the round trip.
                      const locked =
                        isSelf && on && (f.key === 'is_superuser' || f.key === 'is_active');
                      return (
                        <div key={f.key} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900">
                              {f.label}
                              {viaAdmin && (
                                <span className="ml-2 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                  already granted via Admin
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{f.hint}</div>
                          </div>
                          <button
                            onClick={() => toggleFlag(u, f.key)}
                            disabled={pendingId === u.id || locked}
                            title={locked ? 'You cannot remove your own access' : undefined}
                            className={`px-4 py-2 text-sm rounded-lg font-medium shrink-0 transition-colors disabled:opacity-50 ${
                              on
                                ? 'border border-red-300 text-red-700 hover:bg-red-50'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {pendingId === u.id ? '…' : on ? 'Remove' : 'Grant'}
                          </button>
                        </div>
                      );
                    })}

                    <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900">Password</div>
                        <div className="text-xs text-gray-500">
                          Sets a new temporary one and shows it once
                        </div>
                      </div>
                      <button
                        onClick={() => resetPassword(u)}
                        disabled={pendingId === u.id}
                        className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 shrink-0 disabled:opacity-50"
                      >
                        {pendingId === u.id ? '…' : 'Reset'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {visible.length === 0 && (
            <div className="bg-white rounded-xl shadow-md px-4 py-8 text-center text-gray-500">
              No users match “{filter}”.
            </div>
          )}
        </div>

        <p className="mt-6 text-sm text-gray-500">
          Administrators pass the Advanced, Expert, Secret Corner and Haiku gates whether or
          not those flags are set, so a row marked “already granted via Admin” has that access
          today. Temporary passwords are shown here once and never emailed.
        </p>
      </div>
    </div>
  );
};

export default AdminUsersPage;
